import { useState, useEffect } from "react";
import {
  MessageCircle,
  Link2,
  Link2Off,
  Bell,
  BarChart3,
  Settings,
  Loader2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { SectionCardSkeleton, toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { useAuth } from "@/shared/hooks/use-auth";

interface LinkedAccount {
  id: string;
  discord_id: string;
  discord_username: string | null;
  discord_avatar: string | null;
  discord_email: string | null;
  status: string;
  linked_at: string;
}

export function DiscordSettingsPage() {
  const { user } = useAuth();
  const [account, setAccount] = useState<LinkedAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const params = new URLSearchParams(window.location.search);
  const justLinked = params.get("linked") === "true";

  useEffect(() => {
    if (justLinked) {
      toast.success("Discord 계정이 연동되었습니다!");
      // Clean URL
      window.history.replaceState({}, "", "/discord/settings");
    }
  }, [justLinked]);

  useEffect(() => {
    loadAccount();
  }, [user]);

  async function loadAccount() {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .schema("discord" as any)
        .from("linked_accounts")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!error && data) {
        setAccount(data as LinkedAccount);
      }
    } catch {
      // Not linked
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6 p-6">
        <SectionCardSkeleton />
        <SectionCardSkeleton />
        <SectionCardSkeleton />
        <SectionCardSkeleton />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageCircle className="size-7 text-[#5865F2]" />
        <div>
          <h1 className="text-xl font-bold">Discord 설정</h1>
          <p className="text-[13px] text-text-tertiary">
            Discord 봇 연동 및 알림 설정을 관리합니다.
          </p>
        </div>
      </div>

      {/* Link Status Section */}
      <DiscordLinkSection
        account={account}
        onUnlink={() => {
          setAccount(null);
        }}
      />

      {/* Guild Settings Section (placeholder) */}
      <section className="rounded-2xl border border-stroke-subtle bg-surface-raised p-6">
        <div className="mb-4 flex items-center gap-2">
          <Settings className="size-5 text-text-tertiary" />
          <h2 className="text-base font-semibold">길드 설정</h2>
        </div>
        <p className="text-[13px] text-text-tertiary">
          {account
            ? "등록된 Discord 서버의 봇 설정을 관리합니다."
            : "Discord 계정을 먼저 연동해주세요."}
        </p>
        {account && (
          <p className="mt-3 text-[12px] text-text-tertiary/60">
            Coming soon
          </p>
        )}
      </section>

      {/* Notification Section (placeholder) */}
      <section className="rounded-2xl border border-stroke-subtle bg-surface-raised p-6">
        <div className="mb-4 flex items-center gap-2">
          <Bell className="size-5 text-text-tertiary" />
          <h2 className="text-base font-semibold">알림 구독</h2>
        </div>
        <p className="text-[13px] text-text-tertiary">
          {account
            ? "키워드 순위 변동, 점수 알림 등을 Discord로 받을 수 있습니다."
            : "Discord 계정을 먼저 연동해주세요."}
        </p>
        {account && (
          <p className="mt-3 text-[12px] text-text-tertiary/60">
            Coming soon
          </p>
        )}
      </section>

      {/* Usage Stats Section (placeholder) */}
      <section className="rounded-2xl border border-stroke-subtle bg-surface-raised p-6">
        <div className="mb-4 flex items-center gap-2">
          <BarChart3 className="size-5 text-text-tertiary" />
          <h2 className="text-base font-semibold">사용 통계</h2>
        </div>
        <p className="text-[13px] text-text-tertiary">
          {account
            ? "최근 커맨드 사용 현황을 확인합니다."
            : "Discord 계정을 먼저 연동해주세요."}
        </p>
        {account && (
          <p className="mt-3 text-[12px] text-text-tertiary/60">
            Coming soon
          </p>
        )}
      </section>
    </div>
  );
}

// ─── Link Status Section ───

function DiscordLinkSection({
  account,
  onUnlink,
}: {
  account: LinkedAccount | null;
  onUnlink: () => void;
}) {
  const [unlinking, setUnlinking] = useState(false);

  async function handleUnlink() {
    if (!account) return;
    if (!window.confirm("Discord 연동을 해제하시겠습니까?")) return;

    setUnlinking(true);
    try {
      const { error } = await supabase
        .schema("discord" as any)
        .from("linked_accounts")
        .update({ status: "revoked" })
        .eq("id", account.id);

      if (error) throw error;
      toast.success("Discord 연동이 해제되었습니다.");
      onUnlink();
    } catch (err: any) {
      toast.error(err.message || "연동 해제에 실패했습니다.");
    } finally {
      setUnlinking(false);
    }
  }

  return (
    <section className="rounded-2xl border border-stroke-subtle bg-surface-raised p-6">
      <div className="mb-4 flex items-center gap-2">
        <Link2 className="size-5 text-text-tertiary" />
        <h2 className="text-base font-semibold">연동 상태</h2>
      </div>

      {account ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-lg bg-[#5865F2]/10 p-3">
            <CheckCircle className="size-5 text-green-500" />
            <div className="flex-1">
              <div className="text-[13px] font-medium">
                {account.discord_username ?? account.discord_id}
              </div>
              <div className="text-[11px] text-text-tertiary">
                ID: {account.discord_id} &middot; 연동일:{" "}
                {new Date(account.linked_at).toLocaleDateString("ko-KR")}
              </div>
            </div>
            <button
              onClick={handleUnlink}
              disabled={unlinking}
              className="flex items-center gap-1.5 rounded-lg border border-stroke-subtle px-3 py-1.5 text-[12px] font-medium text-text-tertiary transition-colors hover:bg-signal-danger/10 hover:text-signal-danger disabled:opacity-50"
            >
              {unlinking ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Link2Off className="size-3.5" />
              )}
              해제
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <XCircle className="mx-auto mb-2 size-8 text-text-tertiary/40" />
          <p className="text-[13px] text-text-tertiary">
            연동된 Discord 계정이 없습니다.
          </p>
          <p className="mt-1 text-[12px] text-text-tertiary/60">
            Discord에서{" "}
            <code className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-[11px]">
              /link-account
            </code>{" "}
            커맨드를 실행하여 연동하세요.
          </p>
        </div>
      )}
    </section>
  );
}
